const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "LI",
  "TR",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);

const HEADER_FALLBACK = "#d6e2f3";

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

function isBoldElement(el) {
  const style = el.getAttribute("style") || "";
  if (el.tagName === "B" || el.tagName === "STRONG") {
    return !/font-weight\s*:\s*(normal|[1-4]00)\b/i.test(style);
  }
  return (
    /font-weight\s*:\s*(bold|[6-9]00)\b/i.test(style) ||
    /mso-bidi-font-weight\s*:\s*bold/i.test(style)
  );
}

function isItalicElement(el) {
  const style = el.getAttribute("style") || "";
  return el.tagName === "I" || el.tagName === "EM" || /font-style\s*:\s*italic/i.test(style);
}

function isUnderlineElement(el) {
  const style = el.getAttribute("style") || "";
  return el.tagName === "U" || /text-decoration[^;]*underline/i.test(style);
}

function applyMarks(text, marks) {
  let html = text;
  if (marks.underline) html = `<u>${html}</u>`;
  if (marks.italic) html = `<em>${html}</em>`;
  if (marks.bold) html = `<strong>${html}</strong>`;
  return html;
}

function renderNodes(nodeList, marks) {
  let html = "";
  Array.from(nodeList).forEach((node) => {
    if (node.nodeType === 3) {
      const text = node.nodeValue.replace(/\u00a0/g, " ").replace(/[ \t\r\n]+/g, " ");
      if (!text || !text.trim()) {
        if (text === " " && html && !html.endsWith(" ") && !html.endsWith("<br>")) {
          html += " ";
        }
        return;
      }
      html += applyMarks(escapeHtml(text), marks);
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "COL" || tag === "COLGROUP") return;
    if (tag === "BR") {
      html += "<br>";
      return;
    }

    const next = {
      bold: marks.bold || isBoldElement(node),
      italic: marks.italic || isItalicElement(node),
      underline: marks.underline || isUnderlineElement(node),
    };

    if (tag === "A") {
      const href = node.getAttribute("href") || "";
      const inner = renderNodes(node.childNodes, next)
        .replace(/^(?:<br>|\s)+/, "")
        .replace(/(?:<br>|\s)+$/, "");
      if (/^https?:\/\//i.test(href) && inner) {
        html += `<a href="${escapeAttr(href)}">${inner}</a>`;
      } else {
        html += inner;
      }
      return;
    }

    const block = BLOCK_TAGS.has(tag) || tag === "TD" || tag === "TH";
    if (block && html && !html.endsWith("<br>")) html += "<br>";
    html += renderNodes(node.childNodes, next);
  });
  return html;
}

function cellHtml(cell) {
  return renderNodes(cell.childNodes, { bold: false, italic: false, underline: false })
    .replace(/ +<br>/g, "<br>")
    .replace(/<br> +/g, "<br>")
    .replace(/^(?:<br>|\s)+/, "")
    .replace(/(?:<br>|\s)+$/, "")
    .replace(/(?:<br>\s*){3,}/g, "<br><br>");
}

function cellIsBold(cell) {
  let hasText = false;
  let allBold = true;

  function walk(node, bold) {
    if (node.nodeType === 3) {
      if (!node.nodeValue.replace(/\u00a0/g, " ").trim()) return;
      hasText = true;
      if (!bold) allBold = false;
      return;
    }
    if (node.nodeType !== 1 || node.tagName === "SCRIPT" || node.tagName === "STYLE") return;
    const next = bold || isBoldElement(node);
    Array.from(node.childNodes).forEach((child) => walk(child, next));
  }

  walk(cell, false);
  return hasText && allBold;
}

function normalizeColor(value) {
  if (!value) return "";
  const token = value.trim().replace(/!important/gi, "").trim().split(/\s+/)[0];
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(token)) return token.toLowerCase();
  const rgb = token.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (!rgb) return "";
  const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, parseInt(n, 10)));
  return `rgb(${parts.join(", ")})`;
}

function cellBackground(cell) {
  const style = cell.getAttribute("style") || "";
  const match = style.match(/(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i);
  return normalizeColor(match ? match[1] : cell.getAttribute("bgcolor") || "");
}

function rgbOf(color) {
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split("").map((char) => char + char).join("");
    const n = parseInt(hex, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const match = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
}

function spanValue(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 2 || n > 20) return "";
  return String(n);
}

function dominantBackground(cells) {
  const counts = new Map();
  cells.forEach((cell) => {
    if (!cell.bg) return;
    counts.set(cell.bg, (counts.get(cell.bg) || 0) + 1);
  });
  let best = "";
  let count = 0;
  counts.forEach((n, color) => {
    if (n > count) {
      best = color;
      count = n;
    }
  });
  return best;
}

function unwrapLayoutTable(table) {
  if (table.rows.length !== 1 || table.rows[0].cells.length !== 1) return table;
  const cell = table.rows[0].cells[0];
  const innerTables = cell.querySelectorAll("table");
  if (innerTables.length !== 1) return table;
  const leftover = cell.cloneNode(true);
  leftover.querySelectorAll("table").forEach((el) => el.remove());
  if (leftover.textContent.replace(/\u00a0/g, " ").trim()) return table;
  return unwrapLayoutTable(innerTables[0]);
}

function cellStyle(cell, header) {
  const styles = [
    "border:1px solid #000",
    "padding:10px 12px",
    "vertical-align:top",
    "text-align:left",
    "color:#111",
    "background-color:#fff",
  ];
  if (header) styles.push("font-weight:700");
  if (cell.bg) styles.push(`background-color:${cell.bg}`);
  const rgb = cell.bg ? rgbOf(cell.bg) : null;
  if (rgb && (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 < 0.55) {
    styles.push("color:#fff");
  }
  return styles.join(";");
}

export function buildPastedTableHtml(tableElement) {
  if (!tableElement || tableElement.tagName !== "TABLE") return "";
  const table = unwrapLayoutTable(tableElement);
  const rows = Array.from(table.rows)
    .map((row) =>
      Array.from(row.cells).map((cell) => ({
        html: cellHtml(cell),
        bg: cellBackground(cell),
        bold: cellIsBold(cell),
        colspan: spanValue(cell.getAttribute("colspan")),
        rowspan: spanValue(cell.getAttribute("rowspan")),
      }))
    )
    .filter((row) => row.length);

  if (!rows.length) return "";

  const headerBg = dominantBackground(rows[0]);
  const headerByBold =
    rows[0].some((cell) => cell.html) && rows[0].filter((cell) => cell.html).every((cell) => cell.bold);
  const headerRow = Boolean(headerBg) || headerByBold;
  if (headerRow) {
    const fill = headerBg || HEADER_FALLBACK;
    rows[0].forEach((cell) => {
      if (!cell.bg) cell.bg = fill;
    });
  }

  const body = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell) => {
          const attrs = [`style="${cellStyle(cell, headerRow && rowIndex === 0)}"`];
          if (cell.colspan) attrs.push(`colspan="${cell.colspan}"`);
          if (cell.rowspan) attrs.push(`rowspan="${cell.rowspan}"`);
          const tag = headerRow && rowIndex === 0 ? "th" : "td";
          return `<${tag} ${attrs.join(" ")}>${cell.html}</${tag}>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<table style="width:100%;border-collapse:collapse"><tbody>${body}</tbody></table>`;
}
